import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Check,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const kpis = [
  { icon: MessageCircle, value: "10M+", label: "Messages sent" },
  { icon: Users, value: "100+", label: "Satisfied clients" },
  { icon: ShieldCheck, value: "99.9%", label: "Delivery reliability" },
  { icon: BarChart3, value: "24/7", label: "Live analytics" },
];

const bullets = [
  "Bulk messaging with smart personalization",
  "No-code automation for campaigns and reminders",
  "Reliable WhatsApp API workflows for teams",
  "Actionable dashboards for delivery and engagement",
];

const Hero = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-gradient-to-b from-amber-50/30 via-white to-orange-50/20 pt-32 pb-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          animate={reduceMotion ? undefined : { x: [0, 80, 0], y: [0, 40, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute -left-20 top-16 h-80 w-80 rounded-full bg-amber-200/40 blur-3xl"
        />
        <motion.div
          animate={reduceMotion ? undefined : { x: [0, -70, 0], y: [0, -30, 0] }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
          className="absolute -right-20 bottom-6 h-80 w-80 rounded-full bg-orange-200/40 blur-3xl"
        />
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/80 px-4 py-2 text-sm font-semibold text-amber-900 backdrop-blur">
            <BadgeCheck className="h-4 w-4 text-orange-500" />
            AI-Powered WhatsApp Business Platform
          </div>

          <h1 className="text-4xl font-black leading-tight text-gray-900 sm:text-5xl lg:text-6xl">
            Supercharge Your
            <span className="bg-gradient-to-r from-amber-600 to-orange-500 bg-clip-text text-transparent">
              {" "}
              WhatsApp Growth
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-700 sm:text-lg">
            Automate your communication, scale campaigns, and keep every
            customer conversation personal with a clean and reliable workflow.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/auth")}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5"
            >
              Get Started
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                const target = document.querySelector("#features");
                if (target) {
                  target.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="rounded-xl border border-amber-200 bg-white px-6 py-3 text-sm font-semibold text-gray-700 hover:border-amber-300 hover:text-amber-800"
            >
              Explore Features
            </button>
          </div>

          <div className="mt-8 grid gap-2 sm:grid-cols-2">
            {bullets.map((bullet) => (
              <div
                key={bullet}
                className="flex items-start gap-2 rounded-lg border border-amber-100 bg-white/80 px-3 py-2 text-sm text-gray-700"
              >
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span>{bullet}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          className="rounded-3xl border border-amber-100 bg-white/90 p-6 shadow-xl backdrop-blur"
        >
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">Live Campaign Pulse</h2>
            <div className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              Active
            </div>
          </div>

          <div className="space-y-3">
            {[
              {
                title: "Festival Campaign",
                detail: "2,450 deliveries in the last hour",
                rate: "98% delivered",
              },
              {
                title: "Abandoned Cart Recovery",
                detail: "1,120 follow-ups automated",
                rate: "31% conversion",
              },
              {
                title: "Support Auto Replies",
                detail: "420 instant replies today",
                rate: "8 min avg resolution",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50/70 to-orange-50/50 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                  <p className="text-xs font-bold text-amber-700">{item.rate}</p>
                </div>
                <p className="mt-1 text-xs text-gray-600">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {kpis.map((kpi) => (
              <div
                key={kpi.label}
                className="rounded-xl border border-amber-100 bg-white p-3"
              >
                <kpi.icon className="h-4 w-4 text-orange-500" />
                <p className="mt-2 text-lg font-bold text-gray-900">{kpi.value}</p>
                <p className="text-xs text-gray-600">{kpi.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between rounded-xl border border-amber-100 bg-white p-3 text-sm">
            <span className="inline-flex items-center gap-2 text-gray-700">
              <Sparkles className="h-4 w-4 text-amber-600" />
              Ready for your first campaign?
            </span>
            <button
              type="button"
              onClick={() => navigate("/auth")}
              className="font-semibold text-amber-700 hover:text-orange-600"
            >
              Start now
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;